// SPDX-License-Identifier: MIT
//
// Pure, dependency-free provider-config model + validation. Kept APART from
// ai-provider.ts (which imports the Supabase client) so the desktop bridge can
// consume the sovereign-endpoint type + validation WITHOUT dragging supabase-js
// into the desktop inference path.
//
// Context: ai-lab pool, Point A (doc 07 §4.A). A "sovereign" provider is an
// OpenAI-compatible endpoint the user *connects* (their own vLLM / LiteLLM) —
// Genesis runs no inference of its own, so the BYOAI / zero-server-inference
// north-star holds. See docs/superpowers/specs/2026-05-25-genesis-sovereign-
// endpoint-point-a-design.md.

// The whitelist mirrors the DB CHECK on public.user_ai_providers.provider_kind.
// 'anthropic' is the only wired web provider today ; 'sovereign' is Point A.
export type AiProviderKind = 'anthropic' | 'gemini' | 'openai' | 'sovereign';

export interface SovereignConfig {
  // OpenAI-compatible base URL, e.g. http://localhost:8000/v1 or a tailnet host.
  endpoint_url: string;
  // LiteLLM alias the request forwards as `model` (ai-lab owns alias→model).
  model: string;
  // Optional — a local vLLM may run keyless.
  api_key?: string | null;
}

export type SovereignValidationError =
  | { ok: false; field: 'endpoint_url'; code: 'required' | 'invalid_url' }
  | { ok: false; field: 'model'; code: 'required' };

export type SovereignValidationResult =
  | { ok: true; value: SovereignConfig }
  | SovereignValidationError;

// Pure validation — no I/O. The real "does this endpoint work" check is a live
// call at magnify time ; this guards the typo / wrong-paste failure mode early
// (mirrors the client/DB shape-check pattern used for Anthropic keys).
export function validateSovereignConfig(input: Partial<SovereignConfig>): SovereignValidationResult {
  const endpoint_url = (input.endpoint_url ?? '').trim();
  if (!endpoint_url) return { ok: false, field: 'endpoint_url', code: 'required' };

  let parsed: URL;
  try {
    parsed = new URL(endpoint_url);
  } catch {
    return { ok: false, field: 'endpoint_url', code: 'invalid_url' };
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { ok: false, field: 'endpoint_url', code: 'invalid_url' };
  }

  const model = (input.model ?? '').trim();
  if (!model) return { ok: false, field: 'model', code: 'required' };

  const api_key = typeof input.api_key === 'string' && input.api_key.length > 0 ? input.api_key : null;
  return { ok: true, value: { endpoint_url, model, api_key } };
}
